/*
 * Copyright (c) 2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { ExtensionEventResponse } from '@cp/customer-database-sdk'
import { BaseUserHelpers } from '../BaseUserHelpers'
import { EventStatusHelpers } from '../event/EventStatusHelpers'
import { ProjectNameHelpers } from '../project/ProjectNameHelpers'
import { DEFAULT_RENDER_FUNCTION, getColumnValue } from './TableColumnHelpers'
import { ColumnDefinitionFn, ColumnRendererRecord, ValidEventColumn } from './types'

const COLUMN_TITLE_MAPPING = {
  name: 'Event Name',
  launchReadinessData: 'Launch Readiness',
  'project.name': 'Project Name',
  'project.organization.name': 'Organization Name',
  'project.organization.deliveryManager.primary': 'Primary Delivery Manager',
  expectedCCU: 'Expected CCU',
  'confirmedEventDate.start': 'Event Start Date',
  'confirmedEventDate.end': 'Event End Date',
  status: 'Status'
} satisfies Record<ValidEventColumn, string>

const DEFAULT_COLUMN_RENDERER_MAPPING = {
  name: undefined,
  launchReadinessData: undefined,
  'project.name': projectName => ProjectNameHelpers.renderName(projectName),
  'project.organization.name': undefined,
  'project.organization.deliveryManager.primary': primaryDM => BaseUserHelpers.renderFullName(primaryDM),
  expectedCCU: expectedCCU => expectedCCU ?? '-',
  'confirmedEventDate.start': confirmedEventStartDate => confirmedEventStartDate ?? '-',
  'confirmedEventDate.end': confirmedEventEndDate => confirmedEventEndDate ?? '-',
  status: status => EventStatusHelpers.getEventStatusInfo(status)
} satisfies ColumnRendererRecord<ExtensionEventResponse, ValidEventColumn>

const DEFAULT_COLUMNS_RECORD = [
  'name',
  'project.name',
  'project.organization.name',
  'project.organization.deliveryManager.primary',
  'expectedCCU',
  'confirmedEventDate.start',
  'confirmedEventDate.end',
  'status'
] satisfies Array<ValidEventColumn>

export class EventTableColumnDefinitionHelpers {
  static VALID_EVENT_COLUMNS = Object.keys(DEFAULT_COLUMN_RENDERER_MAPPING) as Array<ValidEventColumn>
  static DEFAULT_COLUMNS_RECORD = DEFAULT_COLUMNS_RECORD

  static getDefaultEventColumnsTitleMapping = (columnsKey: Array<ValidEventColumn> | undefined = DEFAULT_COLUMNS_RECORD) => {
    const columnTitle: string[] = []
    for (const key of columnsKey) {
      const mappedColumnTitleFromKey = COLUMN_TITLE_MAPPING[key]
      if (mappedColumnTitleFromKey) {
        columnTitle.push(mappedColumnTitleFromKey)
      }
    }

    return columnTitle
  }

  static getEventColumn: ColumnDefinitionFn<ExtensionEventResponse, ValidEventColumn> = (param, renderFn) => ({
    name: param,
    title: COLUMN_TITLE_MAPPING[param],
    render: record => {
      const effectiveRenderFn = renderFn ?? (DEFAULT_COLUMN_RENDERER_MAPPING[param] as typeof renderFn) ?? DEFAULT_RENDER_FUNCTION
      const effectiveValue = getColumnValue(record, param)
      return effectiveRenderFn(effectiveValue, record)
    }
  })
}
